import { useMixerMenuItem, useTunerMenuItem } from '@tunasong/audio-ui'
import { Brain, Compose, ElectricGuitar, Fretboard, Functions, Headset, LibraryMusic, Metronome, MusicNote, OpenLink, Transform } from '@tunasong/icons'
import { useKeyboardMenuItem } from '@tunasong/music-ui'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Typography,
  type TypographyProps,
  useNavigate,
  useUserConfig,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@tunasong/ui-lib'
import { type FC } from 'react'
import styles from './tools-menu-items.module.css'

export interface ToolsMenuItemsProps {
  entity?: Persisted<Entity>
  variant?: TypographyProps['variant']
  onClose?: () => void
}

export const ToolsMenuItems: FC<ToolsMenuItemsProps> = props => {
  const { onClose, variant } = props

  const { dialog: mixerDialog, menuItem: mixerMenuItem } = useMixerMenuItem({
    variant,
    onClose,
  })
  const { dialog: tunerDialog, menuItem: tunerMenuItem } = useTunerMenuItem({ variant, onClose })
  const { dialog: keyboardDialog, menuItem: keyboardMenuItem } = useKeyboardMenuItem({
    variant,
    onClose,
  })

  const navigate = useNavigate()
  const [externalTools = []] = useUserConfig('externalTools')

  return (
    <>
      {mixerMenuItem}
      {tunerMenuItem}
      {keyboardMenuItem}

      <MenuItem onClick={() => void navigate('/app/guitarchords')}>
        <ListItemIcon className={styles.listItemIcon}>
          <ElectricGuitar />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Guitar Chords</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/guitarpatterns')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Fretboard />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Guitar Patterns</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/theory')}>
        <ListItemIcon className={styles.listItemIcon}>
          <MusicNote />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Music Theory</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/modes')}>
        <ListItemIcon className={styles.listItemIcon}>
          <LibraryMusic />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Mode Lab</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/quiz')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Brain />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Function Quiz</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/reharm')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Transform />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Reharm Lab</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/cadences')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Functions />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Cadence Lab</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/melody')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Compose />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Melody Writer</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/ear-training')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Headset />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Ear Training</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => void navigate('/app/rhythm')}>
        <ListItemIcon className={styles.listItemIcon}>
          <Metronome />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Rhythm Trainer</Typography>
        </ListItemText>
      </MenuItem>

      {externalTools.length > 0 ? (
        <MenuItem disabled>
          <Typography variant="caption">External Tools</Typography>
        </MenuItem>
      ) : null}
      {externalTools.map((tool, index) => (
        <MenuItem
          key={index}
          onClick={() => {
            void navigate(
              `/app/external-tool?url=${encodeURIComponent(tool.url)}&name=${encodeURIComponent(tool.name)}`
            )
            onClose?.()
          }}
        >
          <ListItemIcon className={styles.listItemIcon}>
            <OpenLink />
          </ListItemIcon>
          <ListItemText>
            <Typography variant={variant}>{tool.name}</Typography>
          </ListItemText>
        </MenuItem>
      ))}

      {mixerDialog}
      {tunerDialog}
      {keyboardDialog}
    </>
  )
}
